import React, { useEffect, useState, useCallback } from 'react';
import { FaTimes, FaWallet, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import './TopupModal.css';
import API from '../services/api';
import axios from 'axios';

const BASE_URL = 'https://api.zyphronex.com/api';

const TopupModal = ({ isOpen, onClose, userDetails, onConfirm }) => {
  const [amount, setAmount] = useState('');
  const [offerId, setOfferId] = useState('1');
  const [txnHash, setTxnHash] = useState('');

  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userToken, setUserToken] = useState(null);
  const [stakeData, setStakeData] = useState(null);

  // ================== CREATE TOKEN ==================
  const createToken = useCallback(async () => {
    if (!userDetails?.id) return;

    setTokenLoading(true);
    setError('');
    setUserToken(null);

    try {
      const formData = new FormData();
      formData.append('user_id', userDetails.id);

      const res = await API.post('/admin/login-as-user', formData);
      const result = res.data;

      if (result.status === true && result.token) {
        setUserToken(result.token);
      } else {
        setError(result.message || 'Failed to generate user token');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate token');
    } finally {
      setTokenLoading(false);
    }
  }, [userDetails?.id]);

  useEffect(() => {
    if (isOpen) createToken();
  }, [isOpen, createToken]);

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen]);

  const resetForm = () => {
    setAmount('');
    setOfferId('1');
    setTxnHash('');
    setError('');
    setSuccess('');
    setUserToken(null);
    setStakeData(null);
    setTokenLoading(false);
    setLoading(false);
    setUpdating(false);
  };

  // ================== SUBMIT STAKE ==================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || !userToken) {
      setError('Please wait for token generation');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setStakeData(null);

    try {
      const formData = new FormData();
      formData.append('stake_amount', numAmount);
      formData.append('offer_id', offerId);
      formData.append('deposit_address', userDetails?.deposit_address || '0x0000000000000000000000000000000000000000');
      formData.append('location', userDetails?.location || 'India');

      const response = await axios.post(`${BASE_URL}/stake`, formData, {
        headers: { Authorization: `Bearer ${userToken}` },
      });

      const result = response.data;

      if (result.status === true) {
        setStakeData(result);
        setSuccess(result.message || 'Stake submitted successfully!');
      } else {
        // ✅ Special handling for your error message
        if (result.message === "Only Trade in higher lock period") {
          setError("❌ You can only stake in a higher lock period. Please select a longer lock duration.");
        } else {
          setError(result.message || 'Stake request failed');
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Stake request failed';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ================== UPDATE STAKE ==================
  const handleUpdateStake = async () => {
    if (!stakeData?.data?.id || !txnHash.trim()) {
      setError('Transaction Hash is required');
      return;
    }

    setUpdating(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('stake_id', stakeData.data.id);
      formData.append('txn_hash', txnHash.trim());

      const response = await axios.post(`${BASE_URL}/updateStake`, formData, {
        headers: { Authorization: `Bearer ${userToken}` },
      });

      const result = response.data;

      if (result.status === true) {
        setSuccess('✅ Stake Updated Successfully!');
        setTimeout(onClose, 2000);
      } else {
        setError(result.message || 'Update failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update stake');
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => !loading && !updating && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><FaWallet /> Topup & Stake</h2>
          <button className="modal-close" onClick={() => !loading && !updating && onClose()} disabled={loading || updating}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <div className="user-info-box">
            <p><strong>User ID:</strong> #{userDetails.id}</p>
            <p><strong>Current Balance:</strong> ${Number(userDetails.available_income).toLocaleString()}</p>
          </div>

          {tokenLoading && <p className="loading-text">🔑 Generating User Token...</p>}

          {/* Error Message */}
          {error && (
            <p className="error-message">
              <FaExclamationTriangle /> {error}
            </p>
          )}

          {/* Success Message */}
          {success && (
            <p className="success-message">
              <FaCheckCircle /> {success}
            </p>
          )}

          {userToken && <p className="success-message">✅ User Token Ready</p>}

          {!stakeData && (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Topup Amount (USD)</label>
                <div className="input-wrapper">
                  <span className="currency-symbol">$</span>
                  <input
                    type="text"
                    className="amount-input"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d*$/.test(val)) setAmount(val);
                    }}
                    disabled={loading || tokenLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Trading Type / Lock Period</label>
                <select
                  className="amount-input"
                  value={offerId}
                  onChange={(e) => setOfferId(e.target.value)}
                  disabled={loading || tokenLoading}
                >
                  <option value="1">No Freeze</option>
                  <option value="6">1 Year Lock</option>
                  <option value="7">3 Year Lock</option>
                  <option value="8">5 Year Lock</option>
                </select>
              </div>

              <div className="quick-amounts">
                <p>Quick Select:</p>
                <div className="quick-buttons">
                  {[100, 500, 1000, 5000].map((val) => (
                    <button
                      key={val}
                      type="button"
                      className="quick-amount-btn"
                      onClick={() => setAmount(val.toString())}
                      disabled={loading || tokenLoading}
                    >
                      ${val}
                    </button>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={onClose} disabled={loading || updating}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-confirm"
                  disabled={loading || !amount || tokenLoading || !userToken}
                >
                  {loading ? 'Processing...' : 'Submit Stake'}
                </button>
              </div>
            </form>
          )}

          {stakeData && (
            <div className="update-section">
              <h4>Update Stake Transaction</h4>
              <p><strong>Stake ID:</strong> {stakeData.data.id}</p>

              <div className="form-group">
                <label>Transaction Hash (TXN)</label>
                <input
                  type="text"
                  className="amount-input"
                  placeholder="0x..."
                  value={txnHash}
                  onChange={(e) => setTxnHash(e.target.value)}
                  disabled={updating}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={onClose} disabled={updating}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-confirm"
                  onClick={handleUpdateStake}
                  disabled={updating || !txnHash.trim()}
                >
                  {updating ? 'Updating...' : 'Update Stake'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopupModal;